import { v4 as uuidv4 } from 'uuid';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { Element } from '../core/types';
import type { Polygon2D } from '../utils/siteGeometry';
import { polygonArea } from '../utils/siteGeometry';
import i18n from '../i18n';

export interface ToposolidGeometryData {
    boundary: Polygon2D; // Closed plan polygon (XY)
    height: number;      // Depth of the solid below the top face
    baseElevation: number; // Elevation of the top (sketch) face
}

export class ToposolidTool extends BaseTool {
    id = 'toposolid';
    name = 'Toposolid';
    cursor = 'crosshair';

    private points: THREE.Vector3[] = [];
    private currentPoint: THREE.Vector3 | null = null;

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.toposolidSketchTheTerrainBoundaryAt'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point);

        // Closing the loop on the first point finishes the sketch (BIM).
        if (this.points.length >= 3 && snapped.distanceTo(this.points[0]) < 0.3) {
            this.createToposolid();
            this.reset();
            return;
        }
        this.points.push(snapped);
        this.updatePreview();
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            if (this.points.length >= 3) {
                this.createToposolid();
                this.reset();
            }
        } else if (event.key === 'Escape') {
            this.reset();
        } else {
            super.onKeyDown(event);
        }
    }

    private reset() {
        this.points = [];
        this.currentPoint = null;
        this.context?.appStore.setToolPreview(null);
    }

    private updatePreview() {
        if (!this.context) return;

        const previewPoints = [...this.points];
        if (this.currentPoint) previewPoints.push(this.currentPoint);
        if (previewPoints.length > 2) previewPoints.push(previewPoints[0].clone());

        this.context.appStore.setToolPreview({
            type: 'polygon',
            points: previewPoints,
            closed: previewPoints.length > 2,
            color: '#4caf50' // Terrain green
        });
    }

    private createToposolid() {
        if (!this.context) return;

        // Flat sketch plane: the picked Z is the top-face elevation.
        const boundary: Polygon2D = this.points.map(p => [p.x, p.y]);
        const baseElevation = this.points[0].z;
        const props = this.context.bimStore.toolProperties;
        const depth = props.toposolidDepth ?? 3.0;

        const toposolid: Element = {
            id: uuidv4(),
            category: 'Toposolid',
            type: 'Toposolid: Grassland',
            name: 'Toposolid',
            parameters: {
                'Area': polygonArea(boundary).toFixed(2) + ' m²',
                'Material': 'Grass',
                'Top Elevation': `${baseElevation.toFixed(2)} m`,
                'Height': `${depth.toFixed(2)} m`,
                'Volume': `${(polygonArea(boundary) * depth).toFixed(1)} m³`,
            },
            constraints: [],
            geometryData: { boundary, height: depth, baseElevation }
        };

        this.context.bimStore.addElement(toposolid);
        this.notify(i18n.t('tools.notify.toposolidCreated', { elevation: baseElevation.toFixed(2) }), 'success');
    }
}
